import { CartLine, Discount } from "~/entities/orderEntities";
import { bundleInstanceIdFromDiscount } from "~/helpers/promotionHelpers";

// Display grouping for bundle deals. Every amount here is summed from the server's line prices and discount
// records; nothing is calculated as a discount on the client.

// The parts of a line grouping needs, so cart lines and the server's order lines (success page) both work
type PricedItem = { qty: number; unitPrice: number; children: PricedItem[] };
type GroupableLine = { id: string; discounts: Discount[]; item: PricedItem };

// A line's price before discounts, modifiers included
export const itemTotal = (item: PricedItem): number =>
  item.qty *
  (item.unitPrice + item.children.reduce((acc, x) => acc + itemTotal(x), 0));

export interface DealMember<L extends GroupableLine = CartLine> {
  line: L;
  // Modifiers and upsizes, charged on top of the deal price
  extras: number;
}

export interface DealGroup<L extends GroupableLine = CartLine> {
  instanceId: string;
  promotionCode: string;
  name: string;
  members: DealMember<L>[];
  // Deal price plus extras
  total: number;
  saving: number;
}

export type CartEntry<L extends GroupableLine = CartLine> =
  | { type: "line"; line: L }
  | { type: "deal"; deal: DealGroup<L> };

const bundleDiscountOf = (line: GroupableLine) => {
  for (const discount of line.discounts) {
    const instanceId = bundleInstanceIdFromDiscount(discount);
    if (instanceId != null && discount.promotionCode) {
      return { discount, instanceId, promotionCode: discount.promotionCode };
    }
  }
  return null;
};

// Lines in cart order, with each deal's lines gathered into one entry at the position of its first line
export const groupCartLines = <L extends GroupableLine = CartLine>(
  lines: L[],
): CartEntry<L>[] => {
  const entries: CartEntry<L>[] = [];
  const deals: Record<string, DealGroup<L>> = {};

  for (const line of lines) {
    const bundle = bundleDiscountOf(line);
    if (!bundle) {
      entries.push({ type: "line", line });
      continue;
    }

    let deal = deals[bundle.instanceId];
    if (!deal) {
      deal = {
        instanceId: bundle.instanceId,
        promotionCode: bundle.promotionCode,
        name: bundle.discount.name,
        members: [],
        total: 0,
        saving: 0,
      };
      deals[bundle.instanceId] = deal;
      entries.push({ type: "deal", deal });
    }

    const linePrice = itemTotal(line.item);
    const saving = bundle.discount.fixedAmount * line.item.qty;
    deal.members.push({
      line,
      extras: linePrice - line.item.unitPrice * line.item.qty,
    });
    deal.total += linePrice - saving;
    deal.saving += saving;
  }

  return entries;
};
